import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  Select,
  HStack,
  VStack,
  Spinner,
  Center,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Textarea,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Grid,
  GridItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tag,
  Avatar,
  Progress,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Switch,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  SimpleGrid,
} from '@chakra-ui/react';
import { 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiEye, 
  FiStar,
  FiWifi,
  FiPhone,
  FiMail,
  FiRefreshCw,
  FiMap,
  FiTarget,
  FiCompass,
} from 'react-icons/fi';
import { 
  getAllTechnicians, 
  Technician, 
  ServiceArea,
  createTechnician,
  updateTechnician,
  deleteTechnician,
} from '../services/firestore';
import TechnicianMap from './TechnicianMap';
import { auth } from '../config/firebase';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { createUser } from '../services/firestore';

const TechnicianManagement: React.FC = () => {
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [filteredTechnicians, setFilteredTechnicians] = useState<Technician[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [skillFilter, setSkillFilter] = useState('all');
  const [selectedTechnician, setSelectedTechnician] = useState<Technician | null>(null);
  
  // Map view state
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [showServiceAreas, setShowServiceAreas] = useState(true);
  const [centerOnSelected, setCenterOnSelected] = useState(true);
  
  // Create/Edit form state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [technicianForm, setTechnicianForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    hourlyRate: '',
    isActive: true,
    skills: [] as string[],
    serviceAreas: [] as ServiceArea[],
    notes: '',
    emergencyContact: {
      name: '',
      phone: '',
      relationship: ''
    },
    vehicleInfo: {
      make: '',
      model: '',
      year: 0,
      color: '',
      licensePlate: ''
    },
    password: '',
    schedule: {
      monday: { start: '09:00', end: '17:00', isAvailable: true },
      tuesday: { start: '09:00', end: '17:00', isAvailable: true },
      wednesday: { start: '09:00', end: '17:00', isAvailable: true },
      thursday: { start: '09:00', end: '17:00', isAvailable: true },
      friday: { start: '09:00', end: '17:00', isAvailable: true },
      saturday: { start: '09:00', end: '17:00', isAvailable: false },
      sunday: { start: '09:00', end: '17:00', isAvailable: false },
    },
  });

  const toast = useToast();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchTechnicians();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    filterTechnicians();
  }, [technicians, searchTerm, statusFilter, skillFilter]);

  const fetchTechnicians = async () => {
    try {
      setLoading(true);
      const data = await getAllTechnicians();
      setTechnicians(data);
    } catch (error) {
      console.error('Error fetching technicians:', error);
      toast({
        title: 'Error loading technicians',
        description: 'Failed to fetch technician data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterTechnicians = () => {
    let filtered = technicians;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(technician =>
        technician.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        technician.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        technician.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        technician.phone.includes(searchTerm)
      );
    }

    // Status filter
    if (statusFilter === 'online') {
      filtered = filtered.filter(technician => technician.isOnline);
    } else if (statusFilter === 'offline') {
      filtered = filtered.filter(technician => !technician.isOnline);
    } else if (statusFilter === 'active') {
      filtered = filtered.filter(technician => technician.isActive);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(technician => !technician.isActive);
    }

    // Skill filter
    if (skillFilter !== 'all') {
      filtered = filtered.filter(technician => 
        technician.skills.includes(skillFilter)
      );
    }

    setFilteredTechnicians(filtered);
  };

  const handleCreateTechnician = async () => {
    try {
      setCreating(true);
      // Validate required fields
      if (!technicianForm.firstName || !technicianForm.lastName || !technicianForm.email || !technicianForm.password) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields including password',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      // 1. Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, technicianForm.email, technicianForm.password);
      const uid = userCredential.user.uid;
      // 2. Create users doc with the same UID
      await createUser({
        email: technicianForm.email,
        firstName: technicianForm.firstName,
        lastName: technicianForm.lastName,
        role: 'technician',
        phone: technicianForm.phone,
        isActive: technicianForm.isActive,
      }, uid);
      // 3. Create technicians doc
      const newTechnician = await createTechnician({
        userId: uid,
        firstName: technicianForm.firstName,
        lastName: technicianForm.lastName,
        email: technicianForm.email,
        phone: technicianForm.phone,
        hourlyRate: parseFloat(technicianForm.hourlyRate) || 0,
        isActive: technicianForm.isActive,
        isOnline: false,
        rating: 0,
        totalReviews: 0,
        skills: technicianForm.skills,
        serviceAreas: technicianForm.serviceAreas,
        notes: technicianForm.notes,
        emergencyContact: technicianForm.emergencyContact,
        vehicleInfo: {
          ...technicianForm.vehicleInfo,
          year: parseInt(technicianForm.vehicleInfo.year.toString()) || 0
        },
        schedule: technicianForm.schedule,
        joinDate: new Date() as any,
        lastActive: new Date() as any,
        performance: {
          totalBookings: 0,
          completedBookings: 0,
          cancelledBookings: 0,
          averageRating: 0,
          totalEarnings: 0,
          averageServiceTime: 0,
          customerSatisfaction: 0,
          onTimePercentage: 0,
          lastMonthBookings: 0,
          lastMonthEarnings: 0
        }
      });
      // Add to local state
      setTechnicians([newTechnician, ...technicians]);
      // Reset form
      resetTechnicianForm();
      setIsCreateOpen(false);
      toast({
        title: 'Technician created',
        description: 'New technician has been created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('Error creating technician:', error);
      toast({
        title: 'Creation failed',
        description: error.message || 'Failed to create technician',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleEditTechnician = (technician: Technician) => {
    setSelectedTechnician(technician);
    setTechnicianForm({
      firstName: technician.firstName,
      lastName: technician.lastName,
      email: technician.email,
      phone: technician.phone,
      hourlyRate: technician.hourlyRate?.toString() || '',
      isActive: technician.isActive,
      skills: technician.skills || [],
      serviceAreas: technician.serviceAreas || [],
      notes: technician.notes || '',
      emergencyContact: technician.emergencyContact || { name: '', phone: '', relationship: '' },
      vehicleInfo: technician.vehicleInfo || { make: '', model: '', year: 0, color: '', licensePlate: '' },
      password: '',
      schedule: technician.schedule || {
        monday: { start: '09:00', end: '17:00', isAvailable: true },
        tuesday: { start: '09:00', end: '17:00', isAvailable: true },
        wednesday: { start: '09:00', end: '17:00', isAvailable: true },
        thursday: { start: '09:00', end: '17:00', isAvailable: true },
        friday: { start: '09:00', end: '17:00', isAvailable: true },
        saturday: { start: '09:00', end: '17:00', isAvailable: false },
        sunday: { start: '09:00', end: '17:00', isAvailable: false },
      },
    });
    setIsEditOpen(true);
  };

  const handleUpdateTechnician = async () => {
    if (!selectedTechnician) return;

    try {
      setUpdating(true);
      
      await updateTechnician(selectedTechnician.id, {
        firstName: technicianForm.firstName,
        lastName: technicianForm.lastName,
        email: technicianForm.email,
        phone: technicianForm.phone,
        hourlyRate: parseFloat(technicianForm.hourlyRate) || 0,
        isActive: technicianForm.isActive,
        skills: technicianForm.skills,
        serviceAreas: technicianForm.serviceAreas,
        notes: technicianForm.notes,
        emergencyContact: technicianForm.emergencyContact,
        vehicleInfo: technicianForm.vehicleInfo,
        schedule: technicianForm.schedule
      });

      // Update local state
      setTechnicians(technicians.map(tech => 
        tech.id === selectedTechnician.id 
          ? { ...tech, ...technicianForm, hourlyRate: parseFloat(technicianForm.hourlyRate) || 0 }
          : tech
      ));

      resetTechnicianForm();
      setIsEditOpen(false);

      toast({
        title: 'Technician updated',
        description: 'Technician has been updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error updating technician:', error);
      toast({
        title: 'Update failed',
        description: 'Failed to update technician',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteTechnician = async (technicianId: string) => {
    if (!window.confirm('Are you sure you want to delete this technician? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      
      await deleteTechnician(technicianId);

      // Remove from local state
      setTechnicians(technicians.filter(tech => tech.id !== technicianId));

      toast({
        title: 'Technician deleted',
        description: 'Technician has been deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Error deleting technician:', error);
      toast({
        title: 'Deletion failed',
        description: 'Failed to delete technician',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const resetTechnicianForm = () => {
    setTechnicianForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      hourlyRate: '',
      isActive: true,
      skills: [],
      serviceAreas: [],
      notes: '',
      emergencyContact: { name: '', phone: '', relationship: '' },
      vehicleInfo: { make: '', model: '', year: 0, color: '', licensePlate: '' },
      password: '',
      schedule: {
        monday: { start: '09:00', end: '17:00', isAvailable: true },
        tuesday: { start: '09:00', end: '17:00', isAvailable: true },
        wednesday: { start: '09:00', end: '17:00', isAvailable: true },
        thursday: { start: '09:00', end: '17:00', isAvailable: true },
        friday: { start: '09:00', end: '17:00', isAvailable: true },
        saturday: { start: '09:00', end: '17:00', isAvailable: false },
        sunday: { start: '09:00', end: '17:00', isAvailable: false },
      },
    });
  };

  const handleFormChange = (field: string, value: any) => {
    setTechnicianForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getStatusColor = (technician: Technician) => {
    if (!technician.isActive) return 'red';
    if (technician.isOnline) return 'green';
    return 'yellow';
  };

  const getStatusText = (technician: Technician) => {
    if (!technician.isActive) return 'Inactive';
    if (technician.isOnline) return 'Online';
    return 'Offline';
  };

  const getTechnicianStats = () => {
    const stats = {
      total: technicians.length,
      online: technicians.filter(t => t.isOnline).length,
      active: technicians.filter(t => t.isActive).length,
      averageRating: technicians.length > 0 
        ? technicians.reduce((sum, t) => sum + (t.rating || 0), 0) / technicians.length 
        : 0,
      totalEarnings: technicians.reduce((sum, t) => sum + (t.performance?.totalEarnings || 0), 0),
      totalBookings: technicians.reduce((sum, t) => sum + (t.performance?.totalBookings || 0), 0)
    };
    return stats;
  };

  const technicianStats = getTechnicianStats();
  const skills = Array.from(new Set(technicians.flatMap(t => t.skills)));

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" color="blue.500" />
      </Center>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6} flexWrap="wrap" gap={4}>
        <Heading 
          size={{ base: 'md', md: 'lg' }}
          pl={{ base: '60px', lg: 0 }}
          pr={{ base: 4, lg: 0 }}
          flex="1"
          minW="200px"
        >
          Technician Management
        </Heading>
        <HStack spacing={3}>
          {/* View Mode Controls */}
          <HStack spacing={2}>
            <Button 
              leftIcon={React.createElement(FiMap as React.ElementType)}
              variant={viewMode === 'list' ? 'outline' : 'solid'}
              colorScheme={viewMode === 'list' ? 'gray' : 'blue'}
              onClick={() => setViewMode('list')}
            >
              List View
            </Button>
            <Button 
              leftIcon={React.createElement(FiMap as React.ElementType)}
              variant={viewMode === 'map' ? 'outline' : 'solid'}
              colorScheme={viewMode === 'map' ? 'gray' : 'blue'}
              onClick={() => setViewMode('map')}
            >
              Map View
            </Button>
          </HStack>
          <Button 
            leftIcon={React.createElement(FiPlus as React.ElementType)} 
            colorScheme="blue"
            onClick={() => setIsCreateOpen(true)}
          >
            Add Technician
          </Button>
        </HStack>
      </Flex>

      {/* Technician Statistics */}
      <Grid templateColumns="repeat(6, 1fr)" gap={4} mb={6}>
        <GridItem>
          <Stat>
            <StatLabel>Total Technicians</StatLabel>
            <StatNumber color="blue.500">{technicianStats.total}</StatNumber>
          </Stat>
        </GridItem>
        <GridItem>
          <Stat>
            <StatLabel>Online Now</StatLabel>
            <StatNumber color="green.500">{technicianStats.online}</StatNumber>
          </Stat>
        </GridItem>
        <GridItem>
          <Stat>
            <StatLabel>Active</StatLabel>
            <StatNumber color="purple.500">{technicianStats.active}</StatNumber>
          </Stat>
        </GridItem>
        <GridItem>
          <Stat>
            <StatLabel>Avg Rating</StatLabel>
            <StatNumber color="orange.500">{(technicianStats.averageRating || 0).toFixed(1)}</StatNumber>
          </Stat>
        </GridItem>
        <GridItem>
          <Stat>
            <StatLabel>Total Earnings</StatLabel>
            <StatNumber color="green.500">${technicianStats.totalEarnings.toLocaleString()}</StatNumber>
          </Stat>
        </GridItem>
        <GridItem>
          <Stat>
            <StatLabel>Total Bookings</StatLabel>
            <StatNumber color="teal.500">{technicianStats.totalBookings}</StatNumber>
          </Stat>
        </GridItem>
      </Grid>

      {/* Search and Filter Controls */}
      <Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
        <HStack spacing={4}>
          <Box flex={1}>
            <FormLabel fontSize="sm">Search Technicians</FormLabel>
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>
          <Box minW="150px">
            <FormLabel fontSize="sm">Status</FormLabel>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </Box>
          <Box minW="150px">
            <FormLabel fontSize="sm">Skills</FormLabel>
            <Select value={skillFilter} onChange={(e) => setSkillFilter(e.target.value)}>
              <option value="all">All Skills</option>
              {skills.map(skill => (
                <option key={skill} value={skill}>{skill}</option>
              ))}
            </Select>
          </Box>
          {viewMode === 'map' && (
            <HStack spacing={2}>
              <Tooltip label="Show Service Areas">
                <IconButton
                  size="md"
                  icon={React.createElement(FiTarget as React.ElementType)}
                  aria-label="Show service areas"
                  colorScheme={showServiceAreas ? 'green' : 'gray'}
                  variant={showServiceAreas ? 'solid' : 'outline'}
                  onClick={() => setShowServiceAreas(!showServiceAreas)}
                />
              </Tooltip>
              <Tooltip label="Center on Selected">
                <IconButton
                  size="md"
                  icon={React.createElement(FiCompass as React.ElementType)}
                  aria-label="Center on selected"
                  colorScheme={centerOnSelected ? 'blue' : 'gray'}
                  variant={centerOnSelected ? 'solid' : 'outline'}
                  onClick={() => setCenterOnSelected(!centerOnSelected)}
                />
              </Tooltip>
              <Tooltip label="Refresh Locations">
                <IconButton
                  size="md"
                  icon={React.createElement(FiRefreshCw as React.ElementType)}
                  aria-label="Refresh locations"
                  colorScheme="purple"
                  onClick={fetchTechnicians}
                  isLoading={loading}
                />
              </Tooltip>
            </HStack>
          )}
        </HStack>
      </Box>

      {/* Content based on view mode */}
      {viewMode === 'list' ? (
        /* Technicians Table */
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <Box 
            overflowX="auto" 
            overflowY="auto"
            maxH="calc(100vh - 400px)"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
          >
            <Table variant="simple" minW="100%">
            <Thead>
              <Tr>
                <Th>Technician</Th>
                <Th>Status</Th>
                <Th>Rating</Th>
                <Th>Skills</Th>
                <Th>Performance</Th>
                <Th>Last Active</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredTechnicians.map((technician) => (
                <Tr key={technician.id}>
                  <Td>
                    <HStack spacing={3}>
                      <Avatar 
                        size="sm" 
                        name={`${technician.firstName} ${technician.lastName}`}
                        src={technician.profileImage}
                      />
                      <Box>
                        <Text fontWeight="bold">{technician.firstName} {technician.lastName}</Text>
                        <Text fontSize="sm" color="gray.600">{technician.email}</Text>
                        <Text fontSize="sm" color="gray.500">{technician.phone}</Text>
                      </Box>
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Badge colorScheme={getStatusColor(technician)}>
                        {getStatusText(technician)}
                      </Badge>
                      {technician.isOnline && (
                        <IconButton
                          size="xs"
                          icon={React.createElement(FiWifi as React.ElementType)}
                          aria-label="Online"
                          colorScheme="green"
                          variant="ghost"
                        />
                      )}
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <Text>{(technician.rating || 0).toFixed(1)}</Text>
                      <IconButton
                        size="sm"
                        icon={React.createElement(FiStar as React.ElementType)}
                        aria-label="Rating"
                        colorScheme="yellow"
                        variant="ghost"
                        minH="44px"
                        minW="44px"
                      />
                      <Text fontSize="sm" color="gray.500">({technician.totalReviews || 0})</Text>
                    </HStack>
                  </Td>
                  <Td>
                    <HStack spacing={1} flexWrap="wrap">
                      {(technician.skills || []).slice(0, 2).map(skill => (
                        <Tag key={skill} size="sm" colorScheme="blue">
                          {skill}
                        </Tag>
                      ))}
                      {(technician.skills || []).length > 2 && (
                        <Text fontSize="sm" color="gray.500">+{(technician.skills || []).length - 2}</Text>
                      )}
                    </HStack>
                  </Td>
                  <Td>
                    <VStack spacing={1} align="start">
                      <Text fontSize="sm">
                        {technician.performance?.completedBookings || 0} completed
                      </Text>
                      <Text fontSize="sm" color="gray.600">
                        ${(technician.performance?.totalEarnings || 0).toLocaleString()}
                      </Text>
                      <Progress 
                        value={technician.performance?.onTimePercentage || 0} 
                        size="xs" 
                        colorScheme="green"
                        w="60px"
                      />
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontSize="sm">
                      {technician.lastActive ? 
                        new Date(technician.lastActive.toDate()).toLocaleDateString() : 
                        'Never'
                      }
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label="View Details">
                        <IconButton
                          size="sm"
                          icon={React.createElement(FiEye as React.ElementType)}
                          aria-label="View details"
                          variant="ghost"
                          minH="44px"
                          minW="44px"
                          onClick={() => {
                            setSelectedTechnician(technician);
                            setIsDetailsOpen(true);
                          }}
                        />
                      </Tooltip>
                      <Tooltip label="Edit Technician">
                        <IconButton
                          size="sm"
                          icon={React.createElement(FiEdit2 as React.ElementType)}
                          aria-label="Edit technician"
                          variant="ghost"
                          minH="44px"
                          minW="44px"
                          onClick={() => handleEditTechnician(technician)}
                        />
                      </Tooltip>
                      <Tooltip label="Delete Technician">
                        <IconButton
                          size="sm"
                          icon={React.createElement(FiTrash2 as React.ElementType)}
                          aria-label="Delete technician"
                          variant="ghost"
                          colorScheme="red"
                          minH="44px"
                          minW="44px"
                          onClick={() => handleDeleteTechnician(technician.id)}
                          isLoading={deleting}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          </Box>

          {filteredTechnicians.length === 0 && (
            <Center py={8}>
              <Text color="gray.500">No technicians found</Text>
            </Center>
          )}
        </Box>
      ) : (
        /* Map View */
        <Box bg="white" p={6} borderRadius="lg" boxShadow="sm">
          <TechnicianMap
            technicians={filteredTechnicians}
            selectedTechnician={selectedTechnician || undefined}
            onTechnicianSelect={setSelectedTechnician}
            showServiceAreas={showServiceAreas}
            centerOnSelected={centerOnSelected}
          />
        </Box>
      )}

      {/* Create Technician Modal */}
      <Modal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} size={{ base: 'full', md: '4xl' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Technician</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <TechnicianForm 
              form={technicianForm}
              onFormChange={handleFormChange}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleCreateTechnician}
              isLoading={creating}
              loadingText="Creating"
            >
              Create Technician
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Technician Modal */}
      <Modal isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} size={{ base: 'full', md: '4xl' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Technician</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <TechnicianForm 
              form={technicianForm}
              onFormChange={handleFormChange}
            />
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsEditOpen(false)}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleUpdateTechnician}
              isLoading={updating}
              loadingText="Updating"
            >
              Update Technician
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Technician Details Drawer */}
      <Drawer isOpen={isDetailsOpen} placement="right" onClose={() => setIsDetailsOpen(false)} size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Technician Details</DrawerHeader>
          <DrawerBody>
            {selectedTechnician && (
              <TechnicianDetails technician={selectedTechnician} />
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

// Technician Form Component
interface TechnicianFormProps {
  form: any;
  onFormChange: (field: string, value: any) => void;
}

const TechnicianForm: React.FC<TechnicianFormProps> = ({ form, onFormChange }) => {
  return (
    <Tabs>
      <TabList>
        <Tab>Basic Info</Tab>
        <Tab>Skills & Areas</Tab>
        <Tab>Contact & Vehicle</Tab>
        <Tab>Schedule</Tab>
      </TabList>

      <TabPanels>
        <TabPanel>
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <FormControl isRequired>
              <FormLabel>First Name</FormLabel>
              <Input
                value={form.firstName}
                onChange={(e) => onFormChange('firstName', e.target.value)}
                placeholder="John"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Last Name</FormLabel>
              <Input
                value={form.lastName}
                onChange={(e) => onFormChange('lastName', e.target.value)}
                placeholder="Doe"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => onFormChange('email', e.target.value)}
                placeholder="john.doe@example.com"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input
                value={form.phone}
                onChange={(e) => onFormChange('phone', e.target.value)}
                placeholder="+1 (555) 123-4567"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Hourly Rate ($)</FormLabel>
              <NumberInput
                value={form.hourlyRate}
                onChange={(_, value) => onFormChange('hourlyRate', value.toString())}
                min={0}
                precision={2}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>

            <FormControl>
              <FormLabel>Active Status</FormLabel>
              <Switch
                isChecked={form.isActive}
                onChange={(e) => onFormChange('isActive', e.target.checked)}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => onFormChange('password', e.target.value)}
                placeholder="Enter password"
              />
            </FormControl>
          </Grid>

          <FormControl mt={4}>
            <FormLabel>Notes</FormLabel>
            <Textarea
              value={form.notes}
              onChange={(e) => onFormChange('notes', e.target.value)}
              placeholder="Additional notes about the technician..."
              rows={3}
            />
          </FormControl>
        </TabPanel>

        <TabPanel>
          <VStack spacing={4} align="stretch">
            <FormControl>
              <FormLabel>Skills</FormLabel>
              <HStack spacing={2} flexWrap="wrap">
                {['Wash', 'Detail', 'Interior', 'Exterior', 'Premium', 'Maintenance'].map(skill => (
                  <Tag
                    key={skill}
                    size="lg"
                    colorScheme={form.skills.includes(skill) ? 'blue' : 'gray'}
                    cursor="pointer"
                    onClick={() => {
                      const newSkills = form.skills.includes(skill)
                        ? form.skills.filter((s: string) => s !== skill)
                        : [...form.skills, skill];
                      onFormChange('skills', newSkills);
                    }}
                  >
                    {skill}
                  </Tag>
                ))}
              </HStack>
            </FormControl>

            <Alert status="info">
              <AlertIcon />
              <Box>
                <AlertTitle>Service Areas</AlertTitle>
                <AlertDescription>
                  Service areas can be configured after technician creation. Use the map view to set up coverage areas.
                </AlertDescription>
              </Box>
            </Alert>
          </VStack>
        </TabPanel>

        <TabPanel>
          <VStack spacing={4} align="stretch">
            <Heading size="md">Emergency Contact</Heading>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl>
                <FormLabel>Name</FormLabel>
                <Input
                  value={form.emergencyContact.name}
                  onChange={(e) => onFormChange('emergencyContact', {
                    ...form.emergencyContact,
                    name: e.target.value
                  })}
                  placeholder="Emergency contact name"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Phone</FormLabel>
                <Input
                  value={form.emergencyContact.phone}
                  onChange={(e) => onFormChange('emergencyContact', {
                    ...form.emergencyContact,
                    phone: e.target.value
                  })}
                  placeholder="Emergency contact phone"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Relationship</FormLabel>
                <Input
                  value={form.emergencyContact.relationship}
                  onChange={(e) => onFormChange('emergencyContact', {
                    ...form.emergencyContact,
                    relationship: e.target.value
                  })}
                  placeholder="Spouse, Parent, etc."
                />
              </FormControl>
            </Grid>

            <Divider />

            <Heading size="md">Vehicle Information</Heading>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <FormControl>
                <FormLabel>Make</FormLabel>
                <Input
                  value={form.vehicleInfo.make}
                  onChange={(e) => onFormChange('vehicleInfo', {
                    ...form.vehicleInfo,
                    make: e.target.value
                  })}
                  placeholder="Toyota"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Model</FormLabel>
                <Input
                  value={form.vehicleInfo.model}
                  onChange={(e) => onFormChange('vehicleInfo', {
                    ...form.vehicleInfo,
                    model: e.target.value
                  })}
                  placeholder="Camry"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Year</FormLabel>
                <Input
                  value={form.vehicleInfo.year}
                  onChange={(e) => onFormChange('vehicleInfo', {
                    ...form.vehicleInfo,
                    year: e.target.value
                  })}
                  placeholder="2020"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Color</FormLabel>
                <Input
                  value={form.vehicleInfo.color}
                  onChange={(e) => onFormChange('vehicleInfo', {
                    ...form.vehicleInfo,
                    color: e.target.value
                  })}
                  placeholder="White"
                />
              </FormControl>

              <FormControl>
                <FormLabel>License Plate</FormLabel>
                <Input
                  value={form.vehicleInfo.licensePlate}
                  onChange={(e) => onFormChange('vehicleInfo', {
                    ...form.vehicleInfo,
                    licensePlate: e.target.value
                  })}
                  placeholder="ABC123"
                />
              </FormControl>
            </Grid>
          </VStack>
        </TabPanel>

        <TabPanel>
          <VStack spacing={4} align="stretch">
            <Alert status="info" mb={4}>
              <AlertIcon />
              <Box>
                <AlertTitle>Weekly Schedule</AlertTitle>
                <AlertDescription>
                  Configure the technician's weekly availability. This schedule will be used for booking assignments.
                </AlertDescription>
              </Box>
            </Alert>

            {(['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const).map((day) => {
              const daySchedule = form.schedule[day];
              const dayLabel = day.charAt(0).toUpperCase() + day.slice(1);
              
              return (
                <Card key={day}>
                  <CardBody>
                    <HStack spacing={4} align="center">
                      <Box minW="100px">
                        <FormControl display="flex" alignItems="center">
                          <Switch
                            isChecked={daySchedule.isAvailable}
                            onChange={(e) => {
                              const newSchedule = { ...form.schedule };
                              newSchedule[day] = {
                                ...daySchedule,
                                isAvailable: e.target.checked
                              };
                              onFormChange('schedule', newSchedule);
                            }}
                            mr={2}
                          />
                          <FormLabel mb={0} fontWeight="bold" minW="80px">
                            {dayLabel}
                          </FormLabel>
                        </FormControl>
                      </Box>

                      {daySchedule.isAvailable && (
                        <>
                          <FormControl>
                            <FormLabel fontSize="sm">Start Time</FormLabel>
                            <Input
                              type="time"
                              value={daySchedule.start}
                              onChange={(e) => {
                                const newSchedule = { ...form.schedule };
                                newSchedule[day] = {
                                  ...daySchedule,
                                  start: e.target.value
                                };
                                onFormChange('schedule', newSchedule);
                              }}
                              size="sm"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="sm">End Time</FormLabel>
                            <Input
                              type="time"
                              value={daySchedule.end}
                              onChange={(e) => {
                                const newSchedule = { ...form.schedule };
                                newSchedule[day] = {
                                  ...daySchedule,
                                  end: e.target.value
                                };
                                onFormChange('schedule', newSchedule);
                              }}
                              size="sm"
                            />
                          </FormControl>
                        </>
                      )}

                      {!daySchedule.isAvailable && (
                        <Text fontSize="sm" color="gray.500" ml={4}>
                          Not available
                        </Text>
                      )}
                    </HStack>
                  </CardBody>
                </Card>
              );
            })}

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const defaultSchedule = {
                  monday: { start: '09:00', end: '17:00', isAvailable: true },
                  tuesday: { start: '09:00', end: '17:00', isAvailable: true },
                  wednesday: { start: '09:00', end: '17:00', isAvailable: true },
                  thursday: { start: '09:00', end: '17:00', isAvailable: true },
                  friday: { start: '09:00', end: '17:00', isAvailable: true },
                  saturday: { start: '09:00', end: '17:00', isAvailable: false },
                  sunday: { start: '09:00', end: '17:00', isAvailable: false },
                };
                onFormChange('schedule', defaultSchedule);
              }}
            >
              Reset to Default Schedule
            </Button>
          </VStack>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

// Technician Details Component
interface TechnicianDetailsProps {
  technician: Technician;
}

const TechnicianDetails: React.FC<TechnicianDetailsProps> = ({ technician }) => {
  return (
    <VStack spacing={6} align="stretch">
      {/* Basic Info */}
      <Card>
        <CardHeader>
          <HStack spacing={3}>
            <Avatar 
              size="lg" 
              name={`${technician.firstName} ${technician.lastName}`}
              src={technician.profileImage}
            />
            <Box>
              <Heading size="md">{technician.firstName} {technician.lastName}</Heading>
              <Text color="gray.600">{technician.email}</Text>
              <HStack spacing={2}>
                <Badge colorScheme={technician.isOnline ? 'green' : 'red'}>
                  {technician.isOnline ? 'Online' : 'Offline'}
                </Badge>
                <Badge colorScheme={technician.isActive ? 'blue' : 'gray'}>
                  {technician.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </HStack>
            </Box>
          </HStack>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={2} spacing={4}>
            <Stat>
              <StatLabel>Rating</StatLabel>
              <StatNumber>{(technician.rating || 0).toFixed(1)} ⭐</StatNumber>
              <StatHelpText>{technician.totalReviews || 0} reviews</StatHelpText>
            </Stat>
            <Stat>
              <StatLabel>Hourly Rate</StatLabel>
              <StatNumber>${technician.hourlyRate || 0}/hr</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Total Earnings</StatLabel>
              <StatNumber>${(technician.performance?.totalEarnings || 0).toLocaleString()}</StatNumber>
            </Stat>
            <Stat>
              <StatLabel>Completed Jobs</StatLabel>
              <StatNumber>{technician.performance?.completedBookings || 0}</StatNumber>
            </Stat>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Performance Metrics */}
      <Card>
        <CardHeader>
          <Heading size="md">Performance Metrics</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between">
              <Text>On-Time Percentage</Text>
              <Text fontWeight="bold">{technician.performance?.onTimePercentage || 0}%</Text>
            </HStack>
            <Progress value={technician.performance?.onTimePercentage || 0} colorScheme="green" />
            
            <HStack justify="space-between">
              <Text>Customer Satisfaction</Text>
              <Text fontWeight="bold">{technician.performance?.customerSatisfaction || 0}%</Text>
            </HStack>
            <Progress value={technician.performance?.customerSatisfaction || 0} colorScheme="blue" />
            
            <HStack justify="space-between">
              <Text>Average Service Time</Text>
              <Text fontWeight="bold">{technician.performance?.averageServiceTime || 0} min</Text>
            </HStack>
          </VStack>
        </CardBody>
      </Card>

      {/* Skills & Service Areas */}
      <Card>
        <CardHeader>
          <Heading size="md">Skills & Service Areas</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text fontWeight="bold" mb={2}>Skills</Text>
              <HStack spacing={2} flexWrap="wrap">
                {(technician.skills || []).map(skill => (
                  <Tag key={skill} colorScheme="blue">
                    {skill}
                  </Tag>
                ))}
              </HStack>
            </Box>
            
            <Box>
              <Text fontWeight="bold" mb={2}>Service Areas</Text>
              {technician.serviceAreas.length > 0 ? (
                <VStack spacing={2} align="stretch">
                  {technician.serviceAreas.map(area => (
                    <HStack key={area.id} justify="space-between">
                      <Text>{area.name}</Text>
                      <Text fontSize="sm" color="gray.600">{area.radius}km radius</Text>
                    </HStack>
                  ))}
                </VStack>
              ) : (
                <Text color="gray.500">No service areas configured</Text>
              )}
            </Box>
          </VStack>
        </CardBody>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <Heading size="md">Contact Information</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={3} align="stretch">
            <HStack>
              {React.createElement(FiPhone as React.ElementType)}
              <Text>{technician.phone}</Text>
            </HStack>
            <HStack>
              {React.createElement(FiMail as React.ElementType)}
              <Text>{technician.email}</Text>
            </HStack>
            {technician.emergencyContact?.name && (
              <Box>
                <Text fontWeight="bold">Emergency Contact</Text>
                <Text>{technician.emergencyContact.name} - {technician.emergencyContact.phone}</Text>
                <Text fontSize="sm" color="gray.600">{technician.emergencyContact.relationship}</Text>
              </Box>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Vehicle Information */}
      {technician.vehicleInfo && (
        <Card>
          <CardHeader>
            <Heading size="md">Vehicle Information</Heading>
          </CardHeader>
          <CardBody>
            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Text fontWeight="bold">Make & Model</Text>
                <Text>{technician.vehicleInfo.make} {technician.vehicleInfo.model}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold">Year & Color</Text>
                <Text>{technician.vehicleInfo.year} {technician.vehicleInfo.color}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold">License Plate</Text>
                <Text>{technician.vehicleInfo.licensePlate}</Text>
              </Box>
            </SimpleGrid>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default TechnicianManagement; 